import React from "react";
import { MarketHistoryCategory, MarketHistoryOverview, MarketHistorySearchParams } from "src/Interfaces/marketHistory";
import { getMarketHistoryChart, getMarketHistoryOverview } from "src/Requests/marketHistory";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";

const WrappedTimeSeriesChart = React.lazy(() => import("src/Components/Timeseries/WrappedTimeSeriesChart")
  .then(module => ({ default: module.WrappedTimeSeriesChart })));
const formatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 20 });
const format = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? formatter.format(value) : "—";
type ChartField = "first" | "closing" | "last" | "volume" | "value" | "redemptionNav" | "low" | "high";
const fieldNames: Record<ChartField, string> = {
  first: "اولین", closing: "قیمت پایانی", last: "آخرین", volume: "حجم معاملات", value: "ارزش معاملات",
  redemptionNav: "NAV ابطال", low: "کمترین", high: "بیشترین",
};

function Amount({ value }: { value: number | null | undefined }) {
  return <span dir="ltr">{format(value)}</span>;
}

export default function HistoryOverview({ search, category }: {
  search: MarketHistorySearchParams; category: MarketHistoryCategory;
}) {
  const [graphs, setGraphs] = React.useState<Graph_Dto[]>([]);
  const [overview, setOverview] = React.useState<MarketHistoryOverview | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const isIndex = category === "indices";
  const isFund = category === "funds";
  const [field, setField] = React.useState<ChartField>(isIndex ? "last" : "closing");

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    Promise.all([
      getMarketHistoryChart(category, search, controller.signal),
      getMarketHistoryOverview(category, search, controller.signal),
    ]).then(([graphs, overview]) => {
        if (controller.signal.aborted) return;
        setGraphs(graphs);
        setOverview(overview);
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setError(true);
        packetErrorHandler(error);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [category, search]);

  const datasets = React.useMemo(() => {
    const names = isFund && field === "closing" ? [fieldNames.closing, fieldNames.redemptionNav] :
      [isIndex && field === "last" ? "مقدار شاخص" : fieldNames[field]];
    return graphs.filter(graph => names.includes(graph.name));
  }, [graphs, field, isFund, isIndex]);
  const last = overview;
  const change = overview?.rangeReturn;
  const fields: ChartField[] = isIndex ? ["last", "low", "high"] : ["closing", "last", "volume", "value"];

  if (loading) return <p role="status" className="text-sm text-gray-500">در حال دریافت نمودار…</p>;
  if (error) return null;
  if (!last) return <p className="text-sm text-gray-500">برای نمودار در بازهٔ انتخابی داده‌ای موجود نیست.</p>;

  const metrics: { label: string; value: number | null | undefined }[] = [
    { label: isIndex ? "آخرین مقدار شاخص" : "قیمت پایانی آخرین روز (ریال)", value: isIndex ? last.last : last.closing },
    ...(isIndex ? [
      { label: "کمترین مقدار در بازه", value: last.minimum },
      { label: "بیشترین مقدار در بازه", value: last.maximum },
    ] : [
      { label: "حجم معاملات بازه", value: last.totalVolume },
      { label: "ارزش معاملات بازه (ریال)", value: last.totalValue },
    ]),
    { label: isIndex || category === "options" ? "تعداد مشاهدات" : "روزهای دارای معامله", value: last.observationCount },
    ...(isFund ? [{ label: "NAV ابطال آخرین روز (ریال)", value: last.redemptionNav }] : []),
  ];
  const details: ("first" | "last" | "low" | "high" | "redemptionNav")[] = isIndex ? ["last", "low", "high"] :
    isFund ? ["first", "last", "low", "high", "redemptionNav"] : ["first", "last", "low", "high"];

  return <section aria-label="نمودار و خلاصهٔ تاریخچه" className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
      <div className="flex items-center gap-2.5">
        <span className="h-5 w-1 rounded-full bg-sky-600" aria-hidden="true" />
        <h2 className="font-bold text-slate-800">{last.symbol}</h2>
      </div>
      <span className="text-xs text-slate-500">{last.fromDate} تا {last.toDate}</span>
    </div>
    <dl className={`grid grid-cols-2 lg:grid-cols-3 ${isFund ? "2xl:grid-cols-6" : "2xl:grid-cols-5"} gap-3 bg-slate-50/60 p-4 sm:p-5 text-sm`}>
      {metrics.map((metric, index) => <div key={metric.label} className={`min-w-0 flex flex-col justify-between gap-3 rounded-lg border p-3.5 ${index === 0 ? "border-sky-100 bg-sky-50/60" : "border-slate-100 bg-white"}`}>
        <dt className="text-[11px] leading-5 text-slate-500">{metric.label}</dt>
        <dd className={`text-lg sm:text-xl font-semibold tabular-nums break-words ${index === 0 ? "text-sky-800" : "text-slate-800"}`}><Amount value={metric.value} /></dd>
      </div>)}
      <div className={`min-w-0 flex flex-col justify-between gap-2 rounded-lg border p-3.5 ${change == null || change === 0 ? "border-slate-100 bg-white" : change > 0 ? "border-emerald-100 bg-emerald-50/60" : "border-rose-100 bg-rose-50/60"}`}>
        <dt className="text-[11px] leading-5 text-slate-500">{isIndex ? "تغییر شاخص در بازه" : "بازده قیمت در بازه"}</dt>
        <dd data-testid="range-return" dir="ltr" className={`text-right text-xl font-semibold tabular-nums ${change == null || change === 0 ? "text-slate-700" : change > 0 ? "text-emerald-700" : "text-rose-700"}`}>
          {change == null ? "—" : `${change > 0 ? "+" : ""}${change.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`}
        </dd>
        <span className="text-[10px] leading-4 text-slate-500">{isIndex ? "آخرین مشاهده نسبت به اولین مشاهده" : "قیمت خام؛ بدون تعدیل و سود نقدی"}</span>
      </div>
    </dl>
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(230px,1fr)] gap-5 border-t border-slate-100 p-4 sm:p-5">
      <div className="min-w-0 flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold text-sm text-slate-700">{isIndex ? "روند شاخص" : "روند معاملات"}</h3>
          <label className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2">مقدار نمودار
            <select aria-label="مقدار نمودار" value={field} onChange={event => setField(event.target.value as ChartField)} className="max-w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-200">
              {fields.map(key => <option key={key} value={key}>{isIndex && key === "last" ? "مقدار شاخص" : isFund && key === "closing" ? "قیمت پایانی و NAV ابطال" : fieldNames[key]}</option>)}
            </select>
          </label>
        </div>
        {datasets.some(series => series.graphPoints.some(point => point.price != null)) ? <div className="h-80 min-w-0" dir="ltr">
          <React.Suspense fallback={<p role="status">در حال نمایش نمودار…</p>}>
            <WrappedTimeSeriesChart dataset={datasets} withDate YValueType="number" className="h-full w-full" />
          </React.Suspense>
        </div> : <p className="text-sm text-gray-500">برای مقدار انتخابی در این بازه داده‌ای موجود نیست.</p>}
      </div>
      <aside aria-label="خلاصهٔ آخرین روز" className="self-start rounded-lg border border-slate-100 bg-slate-50/70 p-4 flex flex-col gap-5 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold text-slate-700">خلاصهٔ آخرین روز</h3><span className="rounded-md bg-white px-2 py-1 text-slate-500 text-[10px]">{last.toDate}</span></div>
        <div>
          <p className="text-[11px] text-slate-500 mb-2">{isIndex ? "مقدار شاخص" : "قیمت پایانی"}</p>
          <p className="text-2xl font-semibold tabular-nums text-slate-800"><Amount value={isIndex ? last.last : last.closing} /> <span className="text-xs font-normal text-slate-400">{isIndex ? "واحد" : "ریال"}</span></p>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-5 border-t border-slate-200/70 pt-4">
          {details.map(key => <div key={key} className={key === "redemptionNav" ? "col-span-2" : ""}>
            <dt className="text-[11px] text-slate-500 mb-1.5">{fieldNames[key]}</dt>
            <dd className="font-medium tabular-nums text-slate-700"><Amount value={last[key]} /></dd>
          </div>)}
        </dl>
      </aside>
    </div>
  </section>;
}
